/**
 * @file INCSUBSidebar.js
 * @description Sidebar navigation component for INCSUB Admin Panel.
 * Handles route switching, tab highlighting, logout functionality, and dispatching route-related paths to Redux store.
 * 
 * @author Govind Dindewad
 * @date 2025-05-15
 */

import React, { useState } from 'react';
import './styleSideBar.css';
import { useDispatch, useSelector } from 'react-redux';
import {
    setPlatformPath,
    setPoolPath,
    setPoolVerificationPath,
    setRouteStatus
} from '../../redux/commonSlice';
import { userLogout } from '../../services/userServices';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router';
import { setComplaintRoute } from '../../features/complaints/complaintsSlice';
import { setRevenuePath } from '../../features/revenue/revenueSlice';
import { setUserRoutePath } from '../../features/users/userSlice';
import { setNotificationRoutePath } from '../../features/notifications/notificationSlice';
import { setUserPaymentPath } from '../../features/userPayments/userPaymentSlice';


/**
 * @function INCSUBSidebar
 * @description
 * React functional component that renders the left-side navigation menu (sidebar)
 * for the INCSUB Admin Panel. Includes tab navigation and a logout feature.
 *
 * - Highlights the active tab based on route state.
 * - Allows navigation between admin views.
 * - Supports logout with session clearing and API interaction.
 * @author govind dindewad
 */
function INCSUBSidebar() {
    const [openTab, setOpenTab] = useState(1);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const routeStatus = useSelector((state) => state.common.routeStatus);

    // Sidebar tab configuration
    const [sidebarTab, setSidebarTab] = useState([
        { tabName: "Role Management", iconBlack: "/assets/icons/sidebar/roleManagementTabIconBlack.svg", iconWhite: "/assets/icons/sidebar/roleManagementTabIconWhite.svg", routeValue: '/rolemanagement' },
        { tabName: "Financials Insights", iconBlack: "/assets/icons/sidebar/financialsInsightsBlackicon.svg", iconWhite: "/assets/icons/sidebar/financialsInsightsWhiteicon.svg", routeValue: '/financialinsights' },
        { tabName: "Pools", iconBlack: "/assets/icons/sidebar/poolsBlackIcon.svg", iconWhite: "/assets/icons/sidebar/poolsWhiteIcon.svg", routeValue: '/pools' },
        { tabName: "Users", iconBlack: "/assets/icons/sidebar/userTabIcon.svg", iconWhite: "/assets/icons/sidebar/userTabiconWhite.svg", routeValue: '/user' },
        { tabName: "Pool Verification", iconBlack: "/assets/icons/sidebar/poolBlackIcon.png", iconWhite: "/assets/icons/sidebar/poolWhiteIcon.png", routeValue: '/poolverification' },
        { tabName: "User Payments", iconBlack: "/assets/icons/sidebar/paymentBlackIcon.png", iconWhite: "/assets/icons/sidebar/paymentWhiteIcon.png", routeValue: '/userPayment' },
        { tabName: "Custom Notification", iconBlack: "/assets/icons/sidebar/notificationBlackIcon.png", iconWhite: "/assets/icons/sidebar/notificationWhiteIcon.png", routeValue: '/customnotification' },
        { tabName: "Platforms", iconBlack: "/assets/icons/sidebar/platformTabBlackicon.svg", iconWhite: "/assets/icons/sidebar/platformTabWhiteicon.svg", routeValue: '/platforms' },
        { tabName: "Complaints", iconBlack: "/assets/icons/sidebar/complaintBlackIcon.svg", iconWhite: "/assets/icons/sidebar/complaintWhiteIcon.svg", routeValue: '/complaints' },
        { tabName: "Feedback", iconBlack: "/assets/icons/sidebar/feedbackTabBlackIcon.svg", iconWhite: "/assets/icons/sidebar/feedbackTabWhiteIcon.svg", routeValue: '/feedback' },
        { tabName: "Settings", iconBlack: "/assets/icons/sidebar/settingBlackIcon.png", iconWhite: "/assets/icons/sidebar/settingWhiteIcon.png", routeValue: '/settings' },
    ]);

    /**
    * @function handleLogout
    * @description
    * Handles user logout by:
    * - Calling the `userLogout` API.
    * - Clearing user data from localStorage.
    * - Redirecting to the login page upon successful logout.
    *
    * @async
    * @returns {Promise<void>}
    *  @author govind dindewad
    */

    const handleLogout = async () => {
        const userData = JSON.parse(localStorage.getItem('userData'));
        const userId = userData?.userId;
        const deviceType = "web";

        try {
            const response = await userLogout(userId, deviceType);
            if ([200, 401].includes(response.status)) {
                localStorage.removeItem('userData');
                localStorage.removeItem('token');
                navigate('/login');
            } else {
                toast.error(response.data.message);
            }
        } catch (err) {
            toast.error('Something went wrong during logout');
        }
    };

    /**
     * @function handleTabClick
     * @description
     * Handles tab selection and navigation within the sidebar.
     * Updates the Redux store with the new route and sets the active tab visually.
     *
     * @param {Object} tab - The clicked tab object containing tab data.
     * @param {number} index - The index of the clicked tab in the sidebar array.
     * @returns {void}
     *  @author govind dindewad
     */

    const handleTabClick = (tab, index) => {
        setOpenTab(index + 1);
        sessionStorage.setItem('routeStatus', tab.routeValue);
        dispatch(setRouteStatus(tab.routeValue));
        dispatch(setPlatformPath(tab.routeValue));
        dispatch(setRevenuePath(tab.routeValue));
        dispatch(setUserRoutePath(tab.routeValue));
        dispatch(setPoolVerificationPath(tab.routeValue));
        dispatch(setNotificationRoutePath(tab.routeValue));
        dispatch(setComplaintRoute(tab.routeValue));
        dispatch(setPoolPath(tab.routeValue));
        dispatch(setUserPaymentPath(tab.routeValue))
    };

    return (
        <div className='INCSUBSidebar'>
            <div className='INCSUBSidebar_container'>
                {sidebarTab.map((tab, index) => (
                    <div
                        key={index}
                        className='INCSUBSidebar_tab_container'
                        onClick={() => handleTabClick(tab, index)}
                    >
                        <div
                            className='INCSUBSidebar_tab'
                            style={{ backgroundColor: routeStatus === tab.routeValue ? "#4880FF" : "#ffffff" }}
                        >
                            <div
                                className='INCSUBSidebar_tab_title'
                                style={{ color: routeStatus === tab.routeValue ? "#ffffff" : "#000000" }}
                            >
                                <div className='INCSUBSidebar_imagecontainer'>
                                    <img
                                        src={routeStatus === tab.routeValue ? tab.iconWhite : tab.iconBlack}
                                        alt={`${tab.tabName} icon`}
                                    />
                                </div>
                                {tab.tabName}
                            </div>
                        </div>

                        {routeStatus === tab.routeValue && (
                            <div className='left_corner_shade'></div>
                        )}
                    </div>
                ))}
            </div>

            {/* Logout button */}
            <div className='INCSUBLogoutButton' onClick={handleLogout}>
                <svg width="19" height="20" viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M3 0C2.20435 0 1.44129 0.316071 0.87868 0.87868C0.316071 1.44129 0 2.20435 0 3V17C0 17.7956 0.316071 18.5587 0.87868 19.1213C1.44129 19.6839 2.20435 20 3 20H9C9.79565 20 10.5587 19.6839 11.1213 19.1213C11.6839 18.5587 12 17.7956 12 17V3C12 2.20435 11.6839 1.44129 11.1213 0.87868C10.5587 0.316071 9.79565 0 9 0H3ZM13.293 5.293C13.4805 5.10553 13.7348 5.00021 14 5.00021C14.2652 5.00021 14.5195 5.10553 14.707 5.293L18.707 9.293C18.8945 9.48053 18.9998 9.73484 18.9998 10C18.9998 10.2652 18.8945 10.5195 18.707 10.707L14.707 14.707C14.5184 14.8892 14.2658 14.99 14.0036 14.9877C13.7414 14.9854 13.4906 14.8802 13.3052 14.6948C13.1198 14.5094 13.0146 14.2586 13.0123 13.9964C13.01 13.7342 13.1108 13.4816 13.293 13.293L15.586 11H7C6.73478 11 6.48043 10.8946 6.29289 10.7071C6.10536 10.5196 6 10.2652 6 10C6 9.73478 6.10536 9.48043 6.29289 9.29289C6.48043 9.10536 6.73478 9 7 9H15.586L13.293 6.707C13.1055 6.51947 13.0002 6.26516 13.0002 6C13.0002 5.73484 13.1055 5.48053 13.293 5.293Z"
                        fill="black"
                    />
                </svg>
                Logout
            </div>
        </div>
    );
}

export default INCSUBSidebar;
